import {
  VStack,
  Heading,
  Text,
  Box,
  useDisclosure,
  HStack,
  Button,
  useToast,
} from "@chakra-ui/react";
import { DateDialog } from "components/ui";
import {
  useGenerateCertificateMutation,
  useGetAllCertificatesByPolicyIdAndIsActiveQuery,
  useGetAllCertificateTemplatesByNameLazyQuery,
  useGetPolicyByIdQuery,
  useGetPolicyDocumentsByPolicyIdQuery,
  useGetPolicyNotesByPolicyIdQuery,
  useUpdatePolicyMutation,
} from "graphql/queries/generated/queries";
import PolicyCertificatesSummary from "pages/spin/shared/PolicyCertificatesSummary";
import PolicyDocumentsSummary from "pages/spin/shared/PolicyDocumentsSummary";
import PolicyNotesSummary from "pages/spin/shared/PolicyNotesSummary";
import PolicySummary from "pages/spin/shared/PolicySummary";
import OnViewPolicyAssetsTable from "pages/spin/shared/view/OnViewPolicyAssetsTable";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

const TEPoliciesViewPage = () => {
  const toast = useToast();
  const [getOnlyActiveCertificates, setGetOnlyActiveCertificates] = useState<boolean>(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { policyId } = useParams();
  const { t } = useTranslation();

  const [getAllCertificateTemplatesByName] = useGetAllCertificateTemplatesByNameLazyQuery({
    variables: {
      name: "base template",
    },
  });

  const { data, loading, error, refetch } = useGetPolicyByIdQuery({
    variables: {
      id: policyId,
    },
  });

  const {
    data: certificateSet,
    loading: certificateSetLoading,
    error: certificateSetError,
    refetch: refetchCertificates,
  } = useGetAllCertificatesByPolicyIdAndIsActiveQuery({
    variables: {
      policy: policyId,
      isValid: getOnlyActiveCertificates,
    },
  });

  const [
    updatePolicy,
    { data: updatePolicyData, loading: updatePolicyLoading, error: updatePolicyError },
  ] = useUpdatePolicyMutation();

  const {
    data: policyDocumentsData,
    loading: policyDocumentsLoading,
    error: policyDocumentsError,
  } = useGetPolicyDocumentsByPolicyIdQuery({
    variables: {
      policyId,
    },
  });

  const {
    data: policyNotesData,
    loading: policyNotesLoading,
    error: policyNotesError,
  } = useGetPolicyNotesByPolicyIdQuery({
    variables: {
      policyId,
    },
  });

  const [
    generateCertificate,
    {
      data: generateCertificateData,
      loading: generateCertificateLoading,
      error: generateCertificateError,
    },
  ] = useGenerateCertificateMutation();

  useEffect(() => {
    if (generateCertificateData) {
      toast({
        title: t("Certificate has been generated."),
        description: t(""),
        status: "success",
        duration: 9000,
        isClosable: true,
      });
    }

    if (generateCertificateError)
      toast({
        title: t("Certificate couldn't be generated."),
        description: t(`Error: ${generateCertificateError.message}`),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
  }, [generateCertificateData, generateCertificateError]);

  if (loading) return <Text>Loading...</Text>;
  if (!data || !data?.policy) return null;

  const {
    policy: { policyAssets, policyType, expiresAt },
  } = data;

  const parsedForTablePolicyAssets = policyAssets.edges.flatMap((e) => {
    const { asset, insuredValue, ...rest } = e.node;
    return {
      ...rest,
      insuredValue: {
        amount: insuredValue?.amount,
        currency: insuredValue?.currency?.code,
      },
      ...asset,
    };
  });

  const isExpired = new Date(expiresAt).getTime() < new Date().getTime();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment

  const { allPolicyDocuments } = policyDocumentsData || {};
  const { allPolicyNotes } = policyNotesData || {};

  const renewOrExtend = async ({ startDate, endDate }: { startDate: string; endDate: string }) => {
    const {
      policy: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        __typename,
        id,
        // eslint-disable-next-line @typescript-eslint/no-shadow
        policyAssets,
        policyDocuments,
        policyNotes,
        totalInsuredPremium,
        totalInsuredValue,
        ...policy
      },
    } = data;
    await updatePolicy({
      variables: {
        input: {
          id: policyId,
          policyData: {
            ...policy,
            brokerId: policy.broker.id,
            contractingPartyId: policy.contractingParty.id,
            insuranceCompanyId: policy.insuranceCompany.id,
            eventEntityId: policy.eventEntity?.id,
            eventLocationEntityId: policy.eventLocationEntity.id,
            issuedAt: startDate,
            expiresAt: endDate,
          },
        },
      },
    });

    await refetch();
  };

  const generateCertificates = async () => {
    const { data: certificateTemplatesData } = await getAllCertificateTemplatesByName();

    if (
      !certificateTemplatesData ||
      !certificateTemplatesData?.allCertificateTemplates ||
      certificateTemplatesData?.allCertificateTemplates?.edges.length === 0
    ) {
      toast({
        title: t("Certificate couldn't be generated."),
        description: t(`Error: Missing certificate template!`),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }

    const {
      allCertificateTemplates: {
        edges: [baseTemplate],
      },
    } = certificateTemplatesData;

    const {
      node: { id: certificateTemplateId },
    } = baseTemplate;
    await generateCertificate({
      variables: {
        input: {
          certificateTemplateId,
          id: policyId,
          language: "en",
          hasPdf: true,
        },
      },
    });

    await refetchCertificates();
  };

  const deletePolicy = () => {
    console.log("TEST");
  };

  const QUICK_ACTIONS = [
    {
      name: t("Edit policy"),
      href: `/spin/te-policies/edit/${policyId}`,
      variant: "primary",
    },
    {
      name: isExpired ? t("Renew policy") : t("Extend policy"),
      action: onOpen,
    },
    {
      name: t("Generate certificate PDFs"),
      action: generateCertificates,
      loading: generateCertificateLoading,
    },
    {
      name: t("Delete policy"),
      action: deletePolicy,
      intent: "danger",
    },
  ];

  const openAllCertificates = () => {
    certificateSet?.allCertificates?.edges?.map(({ node }) => window.open(node?.file));
  };

  return (
    <>
      <Box backgroundColor="transparent">
        <PolicySummary policy={data.policy} actions={QUICK_ACTIONS} />
        <VStack my={16} align="flex-start">
          <Heading size="md">
            <Trans>Assets</Trans>
          </Heading>
          <Text variant="muted">Lorem ipsum dolor sit amet</Text>

          {/* TODO: refactor this */}
          <OnViewPolicyAssetsTable
            data={parsedForTablePolicyAssets}
            type="TEMPORARY_EXHIBITION"
            mode="view"
          />
        </VStack>
        <VStack my={16} align="flex-start">
          <Heading size="md">
            <Trans>Notes</Trans>
          </Heading>
          <PolicyNotesSummary policyNotes={allPolicyNotes?.edges.map(({ node }) => node)} />
        </VStack>
        <VStack my={16} align="flex-start">
          <Heading size="md">
            <Trans>Documents</Trans>
          </Heading>
          <PolicyDocumentsSummary
            policyDocuments={allPolicyDocuments?.edges.map(({ node }) => node)}
          />
        </VStack>
        <VStack my={16} align="flex-start">
          <HStack>
            <Heading size="md">
              <Trans>{t("Emitted certificates")}</Trans>
            </Heading>
            <Button variant="ghost" onClick={openAllCertificates}>
              <Text decoration="underline">
                <Trans>Download all</Trans>
              </Text>
            </Button>
            <Button
              variant="ghost"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={async () => {
                setGetOnlyActiveCertificates((prevState) => !prevState);
                await refetchCertificates({ isValid: getOnlyActiveCertificates, policy: policyId });
              }}
            >
              <Text decoration="underline">
                {getOnlyActiveCertificates ? (
                  <Trans>Show only invalid</Trans>
                ) : (
                  <Trans>Show only valid</Trans>
                )}
              </Text>
            </Button>
          </HStack>
          <PolicyCertificatesSummary
            policyCertificates={certificateSet?.allCertificates?.edges?.map(({ node }) => node)}
            refetchCertificates={refetchCertificates}
            loading={certificateSetLoading}
          />
        </VStack>
      </Box>
      <DateDialog
        isOpen={isOpen}
        onClose={onClose}
        onOpen={onOpen}
        title="Update policy expiration"
        message="Set new starting date and expiration date"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={renewOrExtend}
      />
    </>
  );
};

export default TEPoliciesViewPage;
